import {IssueTypeToken} from '@github-ui/issue-type-token'
import {IssueOpenedIcon} from '@primer/octicons-react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueEventRationales$data} from './__generated__/IssueEventRationales.graphql'
import type {IssueTypeChangedEvent$key} from './__generated__/IssueTypeChangedEvent.graphql'
import {IssueEventRationale} from './IssueEventRationale'
import styles from './IssueTypeEvent.module.css'
import {TimelineRow} from './row/TimelineRow'

type IssueTypeChangedEventProps = {
  queryRef: IssueTypeChangedEvent$key & {createdAt?: string}
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLDivElement | null>
  repositoryNameWithOwner?: string
  rationales?: Record<string, IssueEventRationales$data['eventRationales'][number]>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueTypeChangedEventFragment = graphql`
  fragment IssueTypeChangedEvent on IssueTypeChangedEvent {
    id
    databaseId
    actor {
      ...TimelineRowEventActor
    }
    createdAt
    issueType {
      name
      color
    }
    prevIssueType {
      name
      color
    }
  }
`

export function IssueTypeChangedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  repositoryNameWithOwner,
  rationales,
}: IssueTypeChangedEventProps) {
  const {actor, createdAt, issueType, prevIssueType, databaseId, id} = useFragment(
    IssueTypeChangedEventFragment,
    queryRef,
  )

  if (!issueType) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId

  const getTooltipText = (isTextTruncated: boolean) => {
    return isTextTruncated ? issueType.name : undefined
  }

  if (!prevIssueType) {
    return null
  }

  const rationale = rationales?.[id]

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={IssueOpenedIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.issueTypeChanged.leading}`}
        <div className={styles.issueTypeTokenWrapper}>
          <IssueTypeToken
            name={prevIssueType.name}
            color={prevIssueType.color}
            href={`/${repositoryNameWithOwner}/issues?q=type:"${prevIssueType.name}"`}
            getTooltipText={getTooltipText}
            size="small"
          />
        </div>
        {`${LABELS.timeline.issueTypeChanged.trailing}`}
        <div className={styles.issueTypeTokenWrapper}>
          <IssueTypeToken
            name={issueType.name}
            color={issueType.color}
            href={`/${repositoryNameWithOwner}/issues?q=type:"${issueType.name}"`}
            getTooltipText={getTooltipText}
            size="small"
          />
        </div>
        {rationale?.rationale && (
          <IssueEventRationale
            rationale={rationale.rationale}
            issueEventDatabaseId={String(databaseId)}
            target={issueType.name}
            analyticsAction="ISSUE_TYPE_CHANGED"
          />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
